import {
  HOLDING_RATE_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import {
  BASIS_POINTS_DIVISOR,
  DEGEN_FEE_CONFIG_POINTS_DIVISOR,
  MAX_OI_CHECK_BUFFER,
} from '@ui/constants/uiConfig';
import { toReadableValue } from '@ui/lib/bignumber';

import { ContractPairsV4, SlippageType } from './types';

export function toPairsV4(data: ContractPairsV4) {
  slippageTypeCheck(data.slippageConfig.slippageType);

  return {
    ...data,
    fundingFeePerSecondP: toReadableValue(
      data.fundingFeePerSecondP,
      USD_DECIMALS,
    ),
    longHoldingFeeRate: toReadableValue(
      BigInt(data.longHoldingFeeRate),
      HOLDING_RATE_DECIMALS,
    ),
    shortHoldingFeeRate: toReadableValue(
      BigInt(data.shortHoldingFeeRate),
      HOLDING_RATE_DECIMALS,
    ),
    slippageConfig: {
      ...data.slippageConfig,
      longThresholdUsd: toReadableValue(
        data.slippageConfig.longThresholdUsd,
        USD_DECIMALS,
      ),
      shortThresholdUsd: toReadableValue(
        data.slippageConfig.shortThresholdUsd,
        USD_DECIMALS,
      ),
      onePercentDepthAboveUsd: toReadableValue(
        data.slippageConfig.onePercentDepthAboveUsd,
        USD_DECIMALS,
      ),
      onePercentDepthBelowUsd: toReadableValue(
        data.slippageConfig.onePercentDepthBelowUsd,
        USD_DECIMALS,
      ),
      slippageLongP: data.slippageConfig.slippageLongP / BASIS_POINTS_DIVISOR,
      slippageShortP: data.slippageConfig.slippageShortP / BASIS_POINTS_DIVISOR,
    },
    feeConfig: {
      ...data.feeConfig,
      // normalize to use decimals
      openFeeP: data.feeConfig.openFeeP / BASIS_POINTS_DIVISOR,
      closeFeeP: data.feeConfig.closeFeeP / BASIS_POINTS_DIVISOR,
      // degen usage, decimals is 5
      shareP: data.feeConfig.shareP / DEGEN_FEE_CONFIG_POINTS_DIVISOR,
      minCloseFeeP:
        data.feeConfig.minCloseFeeP / DEGEN_FEE_CONFIG_POINTS_DIVISOR,
    },
    leverageMargins: data.leverageMargins.map((leverageMargin) => {
      return {
        ...leverageMargin,
        initialLostP: leverageMargin.initialLostP / BASIS_POINTS_DIVISOR,
        liqLostP: leverageMargin.liqLostP / BASIS_POINTS_DIVISOR,
        notionalUsdBuffered: toReadableValue(
          leverageMargin.notionalUsd,
          USD_DECIMALS,
          /**
           * Adding this due to this check both happens in FE and SC, but will use diff price,
           *
           * to avoid these price diff, we will use a buffer value to check the maxLongOIUsd
           */
        ).multipliedBy(1 - MAX_OI_CHECK_BUFFER),
      };
    }),
    maxFundingFeeR: toReadableValue(data.maxFundingFeeR, USD_DECIMALS),
    maxLongOiUsdBuffered: toReadableValue(
      data.maxLongOiUsd,
      USD_DECIMALS,
      /**
       * Adding this due to this check both happens in FE and SC, but will use diff price,
       *
       * to avoid these price diff, we will use a slippage value to check the maxLongOIUsd
       */
    ).multipliedBy(1 - MAX_OI_CHECK_BUFFER),
    maxShortOiUsdBuffered: toReadableValue(
      data.maxShortOiUsd,
      USD_DECIMALS,
      /**
       * Adding this due to this check both happens in FE and SC, but will use diff price,
       *
       * to avoid these price diff, we will use a slippage value to check the maxLongOIUsd
       */
    ).multipliedBy(1 - MAX_OI_CHECK_BUFFER),
    minFundingFeeR: toReadableValue(data.minFundingFeeR, USD_DECIMALS),
  };
}

const slippageTypeCheck = (slippageType: SlippageType) => {
  switch (slippageType) {
    case SlippageType.FIXED:
    case SlippageType.ONE_PERCENT_DEPTH:
      break;
    default:
      throw new Error(`Invalid slippage type: ${slippageType}`);
  }
};
